import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// One "Install app" button that works everywhere:
//  - Android / desktop Chrome/Edge: captures beforeinstallprompt → native install dialog.
//  - iOS Safari: no programmatic install exists → shows the Share → Add to Home Screen steps.
//  - Already installed (standalone) or not installable: renders nothing.
export function InstallAppButton({ style, compact }: { style?: React.CSSProperties; compact?: boolean }) {
  const { t } = useTranslation('common');
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  // iPhone/iPod, classic iPad, AND iPadOS 13+ which reports as "MacIntel" but is touch.
  const isIOS = (/iphone|ipad|ipod/i.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
    && !(window as unknown as { MSStream?: unknown }).MSStream;

  useEffect(() => {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || (navigator as unknown as { standalone?: boolean }).standalone;
    if (standalone) { setInstalled(true); return; }
    const onPrompt = (e: Event) => { e.preventDefault(); setDeferred(e as BeforeInstallPromptEvent); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;
  if (!deferred && !isIOS) return null;   // not installable on this browser (yet)

  const onClick = async () => {
    if (deferred) {
      await deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      setDeferred(null);
    } else {
      setShowIos(true);
    }
  };

  const btnStyle: React.CSSProperties = compact
    ? { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)', color: '#bcd0ff', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }
    : { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.4)', color: '#bcd0ff', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };

  return (
    <>
      <button type="button" onClick={onClick} style={{ ...btnStyle, ...style }}>
        <svg width={compact ? 13 : 16} height={compact ? 13 : 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
        </svg>
        {t('install.cta', { defaultValue: 'Установить приложение' })}
      </button>

      {showIos && createPortal(
        <div onClick={() => setShowIos(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 2000 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#161a24', borderTop: '1px solid rgba(74,158,255,0.3)', borderRadius: '16px 16px 0 0', padding: '20px 20px calc(28px + env(safe-area-inset-bottom))', width: '100%', maxWidth: 480, color: 'var(--text)' }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{t('install.iosTitle', { defaultValue: 'Установка на iPhone' })}</div>
            <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9, color: 'var(--muted)', fontSize: 14 }}>
              <li>{t('install.iosStep1', { defaultValue: 'Нажмите кнопку «Поделиться» в панели Safari.' })}</li>
              <li>{t('install.iosStep2', { defaultValue: 'Выберите «На экран «Домой»».' })}</li>
              <li>{t('install.iosStep3', { defaultValue: 'Нажмите «Добавить» — готово.' })}</li>
            </ol>
            <button type="button" onClick={() => setShowIos(false)}
              style={{ marginTop: 18, width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
              {t('install.close', { defaultValue: 'Понятно' })}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
